// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { Close } from '@mui/icons-material';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Fade,
    Typography,
    Tooltip,
    styled,
    TooltipProps,
    tooltipClasses,
    IconButton,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import {
    AriaAttributes,
    cloneElement,
    ReactElement,
    useEffect,
    useId,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

const styleTooltipContainer = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
    gap: '8px',

    '& svg': {
        height: '16px',
        width: '16px',
    },
    '& svg path': {
        fill: 'white',
    },
};

const styleIconButton = {
    padding: 0,
};

// Fade handles opacity; this adds the slide via isFadeSlideVisible.
const FADE_SLIDE_DURATION_MS = 350;
const FADE_SLIDE_DISTANCE_PX = 6;

const styleFadeSlide = (isVisible: boolean) => ({
    transition: `transform ${FADE_SLIDE_DURATION_MS}ms ease`,
    transform: isVisible
        ? 'translateY(0)'
        : `translateY(${FADE_SLIDE_DISTANCE_PX}px)`,
});

// Counter-rotates the arrow's gradient to offset its own 45deg tilt.
const ARROW_ROTATION_DEG = 45;

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => {
    const isDark = theme.palette.mode === 'dark';
    const arrowGradientAngle = (isDark ? 180 : 135) - ARROW_ROTATION_DEG;
    const arrowGradientTop = isDark
        ? DS_COLOR.DARK_GRADIENT_ICON_PRIMARY_TOP
        : DS_COLOR.LIGHT_GRADIENT_ICON_PRIMARY_TOP;
    const arrowGradientBottom = isDark
        ? DS_COLOR.DARK_GRADIENT_ICON_PRIMARY_BOTTOM
        : DS_COLOR.LIGHT_GRADIENT_ICON_PRIMARY_BOTTOM;

    return {
        [`& .${tooltipClasses.tooltip}`]: {
            background: theme.palette.gradient.iconPrimary,
            maxWidth: '95dvw',

            [`& > span.${tooltipClasses.arrow}::before`]: {
                background: `linear-gradient(${arrowGradientAngle}deg, ${arrowGradientTop} 0%, ${arrowGradientBottom} 100%)`,
            },
        },
    };
});

interface CHTooltipProps {
    isOpen?: boolean;
    showClose?: boolean;
    // Needs to be 16x16, so remove the height and width props from the SVG file
    startIcon?: ReactElement;
    topLabel?: string;
    content: string;
    bottomLabel?: string;
    // Mui Tooltip expects children to be a single ReactElement
    children: ReactElement<AriaAttributes>;
    arrowPlacement?: TooltipProps['placement'];
    /** Longer fade + slight upward slide, instead of MUI's default Grow. */
    fadeSlideUp?: boolean;
    /** Positions against the viewport instead of the document - for an
     *  anchor that stays put while the page scrolls (e.g. inside a sticky
     *  container), which would otherwise leave the tooltip lagging behind. */
    isFixedPosition?: boolean;
    /** Called when the close (X) button is clicked. */
    onClose?: () => void;
    /** Renders the tooltip in place instead of in a portal, so a modal's focus trap includes its Close button. */
    disablePortal?: boolean;
}

export const CHTooltip = ({
    startIcon,
    topLabel,
    content,
    bottomLabel,
    isOpen = true,
    showClose = true,
    children,
    arrowPlacement = 'top',
    fadeSlideUp = false,
    isFixedPosition = false,
    onClose,
    disablePortal = false,
}: CHTooltipProps) => {
    const { formatMessage } = useIntl();
    const [isTooltipOpen, setIsTooltipOpen] = useState(isOpen);
    const [isFadeSlideVisible, setIsFadeSlideVisible] = useState(false);
    const topLabelId = useId();
    const contentId = useId();
    const labelId = topLabel ? topLabelId : contentId;

    useEffect(() => {
        setIsTooltipOpen(isOpen);
    }, [isOpen]);

    // MUI sets the child's aria-labelledby to the popper's id but spreads the child's own props
    // last, so these win: the child keeps its name and is described by the text, not by Close.
    const describedBy = [
        children.props['aria-describedby'],
        ...(isTooltipOpen ? [topLabel && topLabelId, contentId] : []),
    ]
        .filter(Boolean)
        .join(' ');
    const anchor = showClose
        ? cloneElement(children, {
              'aria-labelledby': children.props['aria-labelledby'],
              'aria-describedby': describedBy || undefined,
          })
        : children;

    return (
        <CustomTooltip
            open={isTooltipOpen}
            arrow
            placement={arrowPlacement}
            // Not slotProps.popper: Tooltip only reads popperOptions here.
            PopperProps={{
                ...(isFixedPosition && {
                    popperOptions: { strategy: 'fixed' },
                }),
                // A tooltip can't hold a control, so one with Close is a non-modal dialog.
                ...(showClose && {
                    role: 'dialog',
                    'aria-labelledby': labelId,
                }),
                disablePortal,
            }}
            {...(fadeSlideUp && {
                TransitionComponent: Fade,
                TransitionProps: {
                    timeout: FADE_SLIDE_DURATION_MS,
                    onEntering: () => setIsFadeSlideVisible(true),
                    onExit: () => setIsFadeSlideVisible(false),
                },
            })}
            title={
                <Box
                    sx={
                        fadeSlideUp
                            ? sxAppend(
                                  styleTooltipContainer,
                                  styleFadeSlide(isFadeSlideVisible)
                              )
                            : styleTooltipContainer
                    }
                >
                    {startIcon}
                    <Box>
                        {topLabel && (
                            <Typography
                                id={topLabelId}
                                variant="subtitle2"
                                component="p"
                                fontSize={10}
                                fontWeight={500}
                            >
                                {topLabel}
                            </Typography>
                        )}
                        <Typography
                            id={contentId}
                            variant="subtitle2"
                            component="p"
                        >
                            {content}
                        </Typography>
                        {bottomLabel && (
                            <Typography
                                variant="subtitle2"
                                component="p"
                                fontSize={10}
                                fontWeight={500}
                            >
                                {bottomLabel}
                            </Typography>
                        )}
                    </Box>
                    {showClose && (
                        <IconButton
                            size="legacyMedium"
                            skipTracking
                            sx={styleIconButton}
                            aria-label={formatMessage(
                                commonMessages['common.close']
                            )}
                            onClick={() => {
                                setIsTooltipOpen(false);
                                onClose?.();
                            }}
                        >
                            <Close />
                        </IconButton>
                    )}
                </Box>
            }
        >
            {anchor}
        </CustomTooltip>
    );
};
